import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import PartnerAuthLayout from "layouts/authentication/components/PartnerAuthLayout";
import { ResetPasswordThunk, checkResetTokenThunk } from "redux/Thunks/Authentication";
import Validations from "utils/Validations/index";
import Constants from "utils/Constants";

function ResetPassword() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, token } = useParams();

  const [password, setPassword] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [error, setError] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenExpire, setTokenExpire] = useState(false);
  const [isNewToken, setIsNewToken] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);

  const handleShowPasswordClick = () => {
    setShowPassword(!showPassword);
  };

  const handleShowConfirmPasswordClick = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleChange = (e) => {
    setPassword((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    setPasswordError("");
  };

  useEffect(() => {
    if (password.confirmPassword !== "") {
      setError(password.newPassword !== password.confirmPassword);
    } else {
      setError(false);
    }
  }, [password]);

  useEffect(() => {
    (async () => {
      if (id && token) {
        try {
          const res = await dispatch(checkResetTokenThunk({ id, token }));
          if (res?.payload?.status === 200) {
            setIsNewToken(true);
          } else {
            setSuccess(true);
            setTokenExpire(true);
          }
        } catch (e) {
          setSuccess(true);
          setTokenExpire(true);
        }
      } else {
        setSuccess(true);
        setTokenExpire(true);
      }
    })();
  }, [id, token, dispatch]);

  useEffect(() => {
    let timer;
    if (success && !tokenExpire) {
      timer = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate("/authentication/sign-in");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [success, tokenExpire, navigate]);

  const handleResetPassword = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setPasswordError("");

    const passwordValidation = Validations.validate("password", password.newPassword, 8, 30, true);
    if (passwordValidation !== "") {
      setPasswordError(passwordValidation);
      return;
    }
    if (password.newPassword !== password.confirmPassword) {
      setError(true);
      return;
    }

    setLoading(true);
    const payload = {
      id,
      token,
      body: {
        password: password.newPassword,
        confirmPassword: password.confirmPassword,
      },
    };

    try {
      const res = await dispatch(ResetPasswordThunk(payload));
      if (res?.payload?.status === 200) {
        setSuccess(true);
        setTokenExpire(false);
      } else if (
        res?.payload?.status === 401 ||
        (res?.payload?.status === 400 &&
          res?.payload?.data?.message?.toLowerCase().includes("token"))
      ) {
        setSuccess(true);
        setTokenExpire(true);
      } else {
        setPasswordError(
          res?.payload?.data?.message || "Failed to reset password. Please try again."
        );
      }
    } catch (err) {
      setPasswordError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleResetPassword(event);
    }
  };

  const renderContent = () => {
    // 1. Verifying Token State
    if (!isNewToken && !success) {
      return (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: { xs: "24px", md: "28px" },
                fontWeight: 800,
                color: "#101828",
                fontFamily: "Inter, sans-serif",
                letterSpacing: "-0.02em",
              }}
            >
              Reset <span style={{ color: "#1E40AF" }}>Password!</span>
            </Typography>
            <Typography sx={{ fontSize: "13.5px", color: "#64748B", mt: 0.5 }}>
              Verifying your password reset link...
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              my: 4,
            }}
          >
            <CircularProgress sx={{ color: "#1E40AF" }} size={40} />
            <Typography sx={{ fontSize: "13.5px", color: "#64748B" }}>
              Please wait while we validate your security token...
            </Typography>
          </Box>
        </Box>
      );
    }

    // 2. Token Expired or Invalid State
    if (success && tokenExpire) {
      return (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: "#FEF2F2",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <ErrorOutlineIcon sx={{ color: "#DC2626", fontSize: "36px" }} />
          </Box>

          <Typography
            sx={{
              fontSize: { xs: "22px", md: "26px" },
              fontWeight: 800,
              color: "#101828",
              fontFamily: "Inter, sans-serif",
              letterSpacing: "-0.02em",
              mb: 1,
            }}
          >
            Reset Link Expired
          </Typography>

          <Typography
            sx={{
              fontSize: "14px",
              color: "#64748B",
              lineHeight: 1.5,
              maxWidth: "360px",
              mx: "auto",
              mb: 3.5,
            }}
          >
            {Constants.PASSWORD_LINK_EXPIRED ||
              "This password reset link is invalid or has expired. Please request a new one."}
          </Typography>

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography
              component={Link}
              to="/authentication/sign-in"
              sx={{
                color: "#1E40AF",
                fontSize: "14px",
                fontWeight: 600,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: "18px" }} /> Back to Log In
            </Typography>
          </Box>
        </Box>
      );
    }

    // 3. Password Reset Successfully State
    if (success && !tokenExpire) {
      return (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: "#F0FDF4",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <CheckCircleOutlineIcon sx={{ color: "#16A34A", fontSize: "36px" }} />
          </Box>

          <Typography
            sx={{
              fontSize: { xs: "22px", md: "26px" },
              fontWeight: 800,
              color: "#101828",
              fontFamily: "Inter, sans-serif",
              letterSpacing: "-0.02em",
              mb: 1,
            }}
          >
            Password Reset Successfully!
          </Typography>

          <Typography
            sx={{
              fontSize: "14px",
              color: "#64748B",
              lineHeight: 1.5,
              maxWidth: "360px",
              mx: "auto",
              mb: 3.5,
            }}
          >
            {Constants.PASSWORD_RESET_SUCCESSFULLY ||
              "Your password has been changed successfully. You can now log in with your new password."}
          </Typography>

          <Button
            component={Link}
            to="/authentication/sign-in"
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: "#1E40AF",
              color: "#FFFFFF",
              height: "46px",
              borderRadius: "8px",
              fontSize: "15px",
              fontWeight: 700,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#1D3A8A",
                boxShadow: "none",
              },
            }}
          >
            Back to Log In
          </Button>

          <Typography sx={{ fontSize: "13px", color: "#64748B", mt: 2.5 }}>
            Redirecting to log in in {redirectCountdown}s...
          </Typography>
        </Box>
      );
    }

    // 4. Active Reset Password Form State
    return (
      <>
        {/* Heading */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              fontSize: { xs: "24px", md: "28px" },
              fontWeight: 800,
              color: "#101828",
              fontFamily: "Inter, sans-serif",
              letterSpacing: "-0.02em",
            }}
          >
            Reset <span style={{ color: "#1E40AF" }}>Password!</span>
          </Typography>
          <Typography sx={{ fontSize: "13.5px", color: "#64748B", mt: 0.5 }}>
            Your new password must be different from previous passwords.
          </Typography>
        </Box>

        {/* Error Notification */}
        {passwordError ? (
          <Box
            sx={{
              backgroundColor: "#FEF2F2",
              border: "1px solid #FEE2E2",
              color: "#991B1B",
              px: 2,
              py: 1.5,
              borderRadius: "8px",
              mb: 2.5,
              fontSize: "13px",
            }}
          >
            {passwordError}
          </Box>
        ) : null}

        {/* Form */}
        <Box component="form" role="form" onSubmit={handleResetPassword}>
          {/* New Password */}
          <Box sx={{ mb: 2 }}>
            <Typography
              component="label"
              htmlFor="newPassword"
              sx={{
                display: "block",
                fontSize: "13px",
                fontWeight: 600,
                color: "#344054",
                mb: 0.6,
              }}
            >
              New Password
            </Typography>
            <TextField
              fullWidth
              id="newPassword"
              name="newPassword"
              type={showPassword ? "text" : "password"}
              placeholder="Enter new password (min. 8 characters)"
              value={password.newPassword}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              error={Boolean(passwordError)}
              helperText={passwordError}
              InputProps={{
                sx: {
                  borderRadius: "8px",
                  fontSize: "14px",
                  backgroundColor: "#FFFFFF",
                  "& fieldset": { borderColor: "#D0D5DD" },
                  "&:hover fieldset": { borderColor: "#98A2B3" },
                  "&.Mui-focused fieldset": { borderColor: "#1E40AF", borderWidth: "1.5px" },
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleShowPasswordClick}
                      edge="end"
                      size="small"
                      sx={{ color: "#98A2B3" }}
                    >
                      {showPassword ? (
                        <VisibilityOutlinedIcon fontSize="small" />
                      ) : (
                        <VisibilityOffOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              FormHelperTextProps={{
                sx: { ml: 0, mt: 0.5, fontSize: "12px", color: "#DC2626" },
              }}
            />
          </Box>

          {/* Confirm Password */}
          <Box sx={{ mb: 2 }}>
            <Typography
              component="label"
              htmlFor="confirmPassword"
              sx={{
                display: "block",
                fontSize: "13px",
                fontWeight: 600,
                color: "#344054",
                mb: 0.6,
              }}
            >
              Confirm Password
            </Typography>
            <TextField
              fullWidth
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm new password"
              value={password.confirmPassword}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              error={Boolean(error)}
              helperText={error ? Constants.PASSWORD_NOT_MATCHED : ""}
              InputProps={{
                sx: {
                  borderRadius: "8px",
                  fontSize: "14px",
                  backgroundColor: "#FFFFFF",
                  "& fieldset": { borderColor: "#D0D5DD" },
                  "&:hover fieldset": { borderColor: "#98A2B3" },
                  "&.Mui-focused fieldset": { borderColor: "#1E40AF", borderWidth: "1.5px" },
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleShowConfirmPasswordClick}
                      edge="end"
                      size="small"
                      sx={{ color: "#98A2B3" }}
                    >
                      {showConfirmPassword ? (
                        <VisibilityOutlinedIcon fontSize="small" />
                      ) : (
                        <VisibilityOffOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              FormHelperTextProps={{
                sx: { ml: 0, mt: 0.5, fontSize: "12px", color: "#DC2626" },
              }}
            />
          </Box>

          {/* Submit Button */}
          <Button
            fullWidth
            variant="contained"
            disabled={loading}
            onClick={handleResetPassword}
            sx={{
              mt: 3,
              backgroundColor: "#1E40AF",
              color: "#FFFFFF",
              height: "46px",
              borderRadius: "8px",
              fontSize: "15px",
              fontWeight: 700,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#1D3A8A",
                boxShadow: "none",
              },
              "&:disabled": {
                backgroundColor: "#93C5FD",
                color: "#FFFFFF",
              },
            }}
          >
            {loading ? "Resetting Password..." : "Reset Password"}
          </Button>

          {/* Back to Log In */}
          <Box sx={{ mt: { xs: 4, md: 6 }, mb: 1, textAlign: "center" }}>
            <Typography
              component={Link}
              to="/authentication/sign-in"
              sx={{
                color: "#1E40AF",
                fontSize: "14px",
                fontWeight: 600,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: "18px" }} /> Back to Log In
            </Typography>
          </Box>
        </Box>
      </>
    );
  };

  return <PartnerAuthLayout>{renderContent()}</PartnerAuthLayout>;
}

export default ResetPassword;
